/**
 * Batch processor utility for parallelizing async operations with concurrency control.
 * Processes items in chunks to avoid overwhelming the server while maximizing throughput.
 */

export interface BatchProcessorOptions<T, R> {
    /** Array of items to process */
    items: T[];
    /** Async function to process each item */
    processor: (item: T) => Promise<R>;
    /** Maximum number of concurrent operations (default: 10) */
    concurrency?: number;
    /** Callback for progress updates */
    onProgress?: (completed: number, total: number) => void;
    /** Whether to continue on individual item failures (default: true) */
    continueOnError?: boolean;
    /** Abandon coopératif : les éléments non encore démarrés sont sautés. */
    signal?: AbortSignal;
}

export interface BatchResult<R> {
    /** Successfully processed results */
    results: R[];
    /** Errors encountered during processing */
    errors: Array<{ index: number; error: any }>;
    /** Total items processed */
    total: number;
    /** Number of successful operations */
    successful: number;
    /** Number of failed operations */
    failed: number;
}

/**
 * Process items in parallel batches with concurrency control.
 * 
 * @example
 * ```typescript
 * const result = await processBatch({
 *   items: assignments,
 *   processor: async (assignment) => updateDistance(assignment),
 *   concurrency: 15,
 *   onProgress: (done, total) => console.log(`${done}/${total}`)
 * });
 * ```
 */
export async function processBatch<T, R>(
    options: BatchProcessorOptions<T, R>
): Promise<BatchResult<R>> {
    const {
        items,
        processor,
        concurrency = 10,
        onProgress,
        continueOnError = true,
        signal,
    } = options;

    const results: R[] = [];
    const errors: Array<{ index: number; error: any }> = [];
    let completed = 0;

    // Process items in chunks of size `concurrency`
    for (let i = 0; i < items.length; i += concurrency) {
        if (signal?.aborted) break;
        const chunk = items.slice(i, i + concurrency);
        const chunkStartIndex = i;

        // Process chunk in parallel
        const chunkResults = await Promise.allSettled(
            chunk.map((item) => processor(item))
        );

        // Collect results and errors
        chunkResults.forEach((result, chunkIndex) => {
            const globalIndex = chunkStartIndex + chunkIndex;

            if (result.status === "fulfilled") {
                results.push(result.value);
            } else {
                errors.push({ index: globalIndex, error: result.reason });

                // If not continuing on error, throw immediately
                if (!continueOnError) {
                    throw new Error(
                        `Batch processing failed at index ${globalIndex}: ${result.reason?.message || result.reason}`
                    );
                }
            }

            completed++;
            if (onProgress) {
                onProgress(completed, items.length);
            }
        });
    }

    return {
        results,
        errors,
        total: items.length,
        successful: results.length,
        failed: errors.length,
    };
}

/**
 * Process items with a simpler API - returns only successful results.
 * Useful when you don't need detailed error tracking.
 */
export async function processBatchSimple<T, R>(
    items: T[],
    processor: (item: T) => Promise<R>,
    concurrency: number = 10
): Promise<R[]> {
    const result = await processBatch({
        items,
        processor,
        concurrency,
        continueOnError: true,
    });
    return result.results;
}
